/** Format a recurring UTC weekly instant as local weekday + time (e.g. "Every Friday at 20:00 MSK"). */
export function formatGuildBattleScheduleLocal(nextBattleAtUtc: string): string {
  const d = new Date(nextBattleAtUtc);
  if (Number.isNaN(d.getTime())) {
    return "Guild battle schedule";
  }
  const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: "long" });
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
  const day = weekdayFmt.format(d);
  const time = timeFmt.format(d);
  const tz = localTimezoneShort(d);
  return tz ? `Every ${day} at ${time} ${tz}` : `Every ${day} at ${time}`;
}

function localTimezoneShort(date: Date): string | undefined {
  return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value;
}

/** Format several UTC times-of-day as local clock times (e.g. "11:00 and 18:00 MSK"). */
export function formatUtcTimesOfDayLocal(
  windows: Array<{ hour: number; minute: number }>,
): string {
  if (!windows.length) return "";
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
  const times = windows.map(({ hour, minute }) => {
    const d = new Date(Date.UTC(2000, 0, 1, hour, minute, 0));
    return timeFmt.format(d);
  });
  const ref = new Date(Date.UTC(2000, 0, 1, windows[0].hour, windows[0].minute, 0));
  const tz = localTimezoneShort(ref);
  const list =
    times.length === 1
      ? times[0]
      : times.length === 2
        ? `${times[0]} and ${times[1]}`
        : `${times.slice(0, -1).join(", ")}, and ${times[times.length - 1]}`;
  return tz ? `${list} ${tz}` : list;
}

/** Party Arena daily UTC windows shown in the player's local timezone. */
export function formatPartyArenaScheduleLocal(
  windows: Array<{ hour: number; minute: number; duration_minutes: number }>,
): string {
  const times = formatUtcTimesOfDayLocal(windows);
  if (!times) return "Schedule unavailable";
  const durationHours = windows[0]?.duration_minutes
    ? windows[0].duration_minutes / 60
    : null;
  const durationPart =
    durationHours != null && Number.isInteger(durationHours)
      ? ` (${durationHours}h sessions)`
      : durationHours != null
        ? ` (${windows[0].duration_minutes} min sessions)`
        : "";
  return `Opens at ${times}${durationPart}`;
}

/** World Boss window range in local time (e.g. "18:00–19:00 MSK"). */
export function formatLocalWindowLabel(utcStartHour: number, durationMinutes: number): string {
  const start = new Date(Date.UTC(2000, 0, 1, utcStartHour, 0, 0));
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
  const tz = localTimezoneShort(start);
  const range = `${timeFmt.format(start)}–${timeFmt.format(end)}`;
  return tz ? `${range} ${tz}` : range;
}
